import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { CustomFormControlAccess } from 'src/app/_core/tools/custom-form-control-access';
import { buildProviders, Coerce } from '@kalidea/kaligraphi';
import { AbstractControl, FormControl } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import {
    CleRepartitionModelDto,
    MutationGetListeClesRepartitionModelArgs,
    TypeCleRepartitionEnum
} from 'src/graphql/generated-types';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import gql from 'graphql-tag';
import { tap } from 'rxjs/operators';
import { wheneverEnd } from 'src/app/utils/operators.utils';

@Component({
    selector: 'app-shared-analytical-plan-distributer-by-model',
    template: `
        <app-shared-analytical-plan-distributer [kalLoading]="loading"
                                                [formControl]="formControl"
                                                [amount]="amount"
                                                [shouldHideAmountColumn]="shouldHideAmountColumn">
        </app-shared-analytical-plan-distributer>`,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedAnalyticalPlanDistributerByModelComponent),
    standalone: false
})
export class SharedAnalyticalPlanDistributerByModelComponent extends CustomFormControlAccess implements OnInit {

    @Input() amount = 0;

    @Input()
    @Coerce('boolean')
    shouldHideAmountColumn = false;

    formControl = new FormControl<CleRepartitionModelDto[] | null>(null);

    private subscription: Subscription;

    constructor(private readonly graphqlService: GraphqlService,
                private readonly cdr: ChangeDetectorRef) {
        super();
    }

    private _modelId = new BehaviorSubject<number>(null);

    get modelId(): number {
        return this._modelId.getValue();
    }

    @Input()
    set modelId(modelId: number) {
        this._modelId.next(modelId);
    }

    get modelId$(): Observable<number> {
        return this._modelId.asObservable();
    }

    private _distributionKeys = new BehaviorSubject<CleRepartitionModelDto[]>(null);

    get distributionKeys(): CleRepartitionModelDto[] {
        return this._distributionKeys.getValue();
    }

    get distributionKeys$(): Observable<CleRepartitionModelDto[]> {
        return this._distributionKeys.asObservable();
    }

    private _loading = false;

    get loading(): boolean {
        return this._loading;
    }

    set loading(value: boolean) {
        this._loading = value;
        this.cdr.markForCheck();
    }

    writeValue(value: CleRepartitionModelDto[]): void {
        this._distributionKeys.next(value);
        super.writeValue(value);
    }

    validate(c: AbstractControl): Observable<{accountAnalyticalRepartition: boolean} | null> {
        return of(this.formControl.valid ? null : {accountAnalyticalRepartition: true});
    }

    private fetchDistributionKeysByEntryModel(): void {
        if(!this.modelId) {
            this.initSubscription();
            return;
        }

        this.loading = true;

        this.watch$ = this.graphqlService.mutateOne<'GetListeClesRepartitionModel', MutationGetListeClesRepartitionModelArgs>({
            mutation: gql`
                mutation GetListeClesRepartitionModel($contexte: ContextInfosInput, $criteres: CriteresCompteSectionAnalytiqueDTOInput) {
                    GetListeClesRepartitionModel(
                        contexte: $contexte
                        criteres: $criteres
                    ) {
                        CleRepartitionModelDTO {
                            IdPlanAnalytique
                            IdSectionAnalytique
                            Montant
                            Pourcentage
                        }
                    }
                }
            `,
            variables: {
                criteres: {
                    IdModeleSaisie: this.modelId,
                    VisionCle: TypeCleRepartitionEnum.Toutes
                }
            }
        }).pipe(
            tap(GetListeClesRepartitionModel => {
                this.formControl.patchValue(this.distributionKeys || GetListeClesRepartitionModel?.CleRepartitionModelDTO || [], {emitEvent: false});
                this.notifyUpdate(this.formControl.value);
                this.initSubscription();
            }),
            wheneverEnd(() => this.loading = false)
        );
    }

    private initSubscription(): void {
        this.subscription = this.formControl.valueChanges.pipe(
            this.untilDestroyed,
            tap(formValues => this.notifyUpdate(formValues))
        ).subscribe();
    }

    ngOnInit(): void {
        this.watch$ = combineLatest([
            this.modelId$,
            this.distributionKeys$
        ]).pipe(
            tap(() => {
                this.subscription?.unsubscribe();

                if (!this.distributionKeys) {
                    this.fetchDistributionKeysByEntryModel();
                } else {
                    this.formControl.patchValue(this.distributionKeys, {emitEvent: false});
                    this.initSubscription();
                }

                this.formControl.updateValueAndValidity({emitEvent: false});
            }),
        );
    }

}
